<script setup lang="ts">
import checkpointApi from '@/services/checkpoint.service'
import { EFiltersActions } from '@/store/filtersStore/FiltersStoreTypes'
import { EStoreModules } from '@/store/storeType'
import {
    defaultCheckpointHubProvider,
    EHubProvider,
    type CheckpointHubProvider
} from '@/types/enum/HubProviderEnum'
import type { EventCheckpointType } from '@/types/filters/EventCheckpointType'
import type { CheckpointModel } from '@/types/models/CheckpointModel'
import { AutocompleteField } from '@rfc/rfc-components-library'
import { EProviders } from '@rfc/rfc-components-library/types'
import { inject, ref, watch, type Ref } from 'vue'
import { useStore } from 'vuex'
import type { EventQueryResponse } from '../../types/filters/EventFilters'

const store = useStore()

const { handleChangeCheckpoint } = inject<CheckpointHubProvider>(
    EHubProvider.CHECKPOINT,
    defaultCheckpointHubProvider
)
const { query } = inject<{ query: Ref<Partial<EventQueryResponse> | undefined> }>(
    EProviders.DETECTION,
    { query: ref(undefined) }
)

type Props = EventCheckpointType & {
    inputValue?: string
    loadingCheckpoints?: boolean
    keepCheckpointInStorage?: boolean
    disabled?: boolean
    noQueryParams?: boolean
    useQueryResponse?: boolean
}
const props = defineProps<Props>()
type Emits = {
    (e: 'typing', value?: string): void
    (e: 'change', value?: CheckpointModel): void
}
const emit = defineEmits<Emits>()

const getCheckpoints = (name?: string) => {
    if (!props.event?.id) return
    checkpointApi
        .getCheckpointsFromEvent(
            props.event.id,
            {
                per_page: -1,
                filters: name ? { name } : undefined
            },
            true
        )
        .then(res => (checkpointOptions.value = res.data ?? res))
}
const checkpointOptions = ref<Partial<CheckpointModel>[]>([])

const handleSelect = (checkpoint?: CheckpointModel) => {
    if (props.keepCheckpointInStorage)
        store?.dispatch(
            `${EStoreModules.FILTERS}/${EFiltersActions.SET_FILTER}`,
            checkpoint ? { checkpoint: { name: checkpoint.name, id: checkpoint.id } } : undefined
        )
    emit('change', checkpoint)
}

watch(
    () => props.event?.id,
    newValue => {
        if (props.useQueryResponse) return
        if (newValue) return getCheckpoints()
        checkpointOptions.value = []
    },
    { immediate: true }
)
watch(
    () => query.value,
    newQueryResponse => {
        if (props.useQueryResponse) checkpointOptions.value = newQueryResponse?.checkpoints ?? []
    }
)
watch(
    () => query?.value?.checkpoint_id,
    newCheckpoint => {
        if (
            newCheckpoint?.id &&
            !checkpointOptions.value.find(
                item => item.id === parseInt(newCheckpoint.id!.toString())
            )
        )
            checkpointOptions.value = [
                { ...newCheckpoint, id: parseInt(newCheckpoint.id.toString()) },
                ...checkpointOptions.value
            ]
        handleChangeCheckpoint?.(
            newCheckpoint?.id
                ? { ...newCheckpoint, id: parseInt(newCheckpoint.id?.toString()) }
                : undefined
        )
    }
)
</script>

<template>
    <AutocompleteField
        :label="$t('device.filters.checkpoint')"
        :modelValue="checkpoint ?? undefined"
        :loading="loadingCheckpoints"
        @typing="$emit('typing', $event)"
        @select="handleSelect($event as CheckpointModel)"
        labelKey="name"
        valueKey="id"
        :options="checkpointOptions"
        :disabled="disabled" />
</template>
